import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { coinMetricsService } from '../services/coinMetricsService';
import { PositionSide, RiskLevel, ComparisonOperator, POSITION_SIDE } from '@/services/enums';

interface OrderTask {
  id: string;
  symbol: string;
  direction: PositionSide;
  entryPrice?: number;
  targetPrice?: number;
  stopLossPrice?: number;
  indicators: Array<{
    name: string;
    value: number | string;
    description: string;
  }>;
  conditions: Array<{
    field: string;
    operator: ComparisonOperator;
    value: number | string;
    description?: string;
  }>;
  riskLevel: RiskLevel;
  positionSize: number;
  createdAt: string;
}

const MyOrders: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const [tasks, setTasks] = useState<OrderTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [removingTaskId, setRemovingTaskId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  // 加载处理中的任务列表
  const loadProcessingTasks = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const tasks = await coinMetricsService.getProcessingOrders();
      setTasks(tasks);
    } catch (error: any) {
      console.error('获取任务列表失败:', error);
      const message =
        error?.response?.status === 403
          ? error.message || '权限不足，无法查看订单任务'
          : error.message || '获取任务列表失败';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  // 移除任务
  const removeTask = async (taskId: string) => {
    if (removingTaskId) return; // 防止重复点击

    try {
      setRemovingTaskId(taskId);
      await coinMetricsService.deleteProcessingOrder(taskId);
      showToast('任务已移除', 'success');
      // 重新加载任务列表
      await loadProcessingTasks();
    } catch (error: any) {
      console.error('移除任务失败:', error);
      showToast(error.message || '移除任务失败', 'error');
    } finally {
      setRemovingTaskId(null);
    }
  };

  useEffect(() => {
    // 防止重复加载（React StrictMode 会执行两次）
    if (hasLoadedRef.current) {
      return;
    }

    hasLoadedRef.current = true;
    loadProcessingTasks();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <GradientBackground>
      <ParticleBackground />
      <div className="min-h-screen">
        {/* 页面头部 */}
        <PageHeader title="我的下单" onBack={() => setCurrentPage('personal-center')} />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
              <div className="text-red-300 text-sm">{errorMessage}</div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-2">暂无处理中的订单</div>
              <div className="text-gray-500 text-sm">所有订单任务完成后将自动移除</div>
            </div>
          ) : (
            <div className="space-y-4">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className={`p-4 rounded-xl border backdrop-blur-sm ${
                    task.direction === POSITION_SIDE.LONG
                      ? 'bg-green-500/10 border-green-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  {/* 任务头部 */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            task.direction === POSITION_SIDE.LONG
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {task.direction === POSITION_SIDE.LONG ? '做多' : '做空'}
                        </span>
                        <span className="text-white font-semibold">{task.symbol}</span>
                      </div>
                      <div className="text-white/60 text-xs mt-1">创建时间: {formatDate(task.createdAt)}</div>
                    </div>
                    <button
                      onClick={() => removeTask(task.id)}
                      disabled={removingTaskId === task.id}
                      className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {removingTaskId === task.id ? '移除中...' : '移除'}
                    </button>
                  </div>

                  {/* 任务详情 */}
                  <div className="space-y-2 text-sm text-white/80">
                    {task.entryPrice && (
                      <div>
                        入场价格: <span className="text-white">{task.entryPrice}</span>
                      </div>
                    )}
                    {task.targetPrice && (
                      <div>
                        止盈价格: <span className="text-green-400">{task.targetPrice}</span>
                      </div>
                    )}
                    {task.stopLossPrice && (
                      <div>
                        止损价格: <span className="text-red-400">{task.stopLossPrice}</span>
                      </div>
                    )}
                    <div>
                      仓位大小: <span className="text-white">{task.positionSize} USDT</span>
                    </div>
                    <div>
                      风险等级:{' '}
                      <span className="text-white">
                        {task.riskLevel === 'low' ? '低风险' : task.riskLevel === 'medium' ? '中风险' : '高风险'}
                      </span>
                    </div>

                    {task.conditions && task.conditions.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/10">
                        <div className="font-semibold mb-1 text-white">触发条件:</div>
                        <ul className="list-disc list-inside space-y-1">
                          {task.conditions.map((cond, idx) => (
                            <li key={idx} className="text-xs">
                              {cond.description || `${cond.field} ${cond.operator} ${cond.value}`}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {task.indicators && task.indicators.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/10">
                        <div className="font-semibold mb-1 text-white">监控指标:</div>
                        <ul className="list-disc list-inside space-y-1">
                          {task.indicators.map((ind, idx) => (
                            <li key={idx} className="text-xs">
                              {ind.name}: {ind.value} - {ind.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </GradientBackground>
  );
};

export default MyOrders;
